import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import type { ShigoSession } from '../features/auth/model';
import { clearRecentSearches } from '../features/search/recentSearches';
import {
  clearShigoSession,
  getBrowserStorage,
  loadShigoSession,
  saveShigoSession,
} from './sessionPersistence';
import { ShigoSessionContext, type ShigoSessionContextValue } from './sessionContext';
import { unauthorizedSessionEvent } from './shigoQueryClient';

export function ShigoSessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSessionState] = useState<ShigoSession | null>(() => {
    const storage = getBrowserStorage();
    return storage ? loadShigoSession(storage) : null;
  });

  const sessionRef = useRef(session);

  const value = useMemo<ShigoSessionContextValue>(
    () => ({
      session,
      setSession: (nextSession) => {
        sessionRef.current = nextSession;
        setSessionState(nextSession);
        const storage = getBrowserStorage();
        if (storage) {
          saveShigoSession(storage, nextSession);
        }
      },
      clearSession: () => {
        if (!sessionRef.current) return;
        sessionRef.current = null;
        setSessionState(null);
        queryClient.clear();
        const storage = getBrowserStorage();
        if (storage) {
          clearShigoSession(storage);
          clearRecentSearches(storage);
        }
      },
      isAuthenticated: Boolean(session?.accessToken),
    }),
    [queryClient, session],
  );

  useEffect(() => {
    const handleUnauthorizedSession = (event: Event) => {
      const accessToken: unknown = event instanceof CustomEvent ? event.detail : undefined;
      if (typeof accessToken === 'string' && sessionRef.current?.accessToken === accessToken) {
        value.clearSession();
      }
    };

    window.addEventListener(unauthorizedSessionEvent, handleUnauthorizedSession);
    return () => {
      window.removeEventListener(unauthorizedSessionEvent, handleUnauthorizedSession);
    };
  }, [session, value]);

  return <ShigoSessionContext.Provider value={value}>{children}</ShigoSessionContext.Provider>;
}
